import { useState, useEffect } from "react";
import { Briefcase, Building, MapPin, Calendar, Star } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Pagination, PaginationContent, PaginationItem, PaginationLink, PaginationNext, PaginationPrevious } from "@/components/ui/pagination";
import { ViewMode } from "@/components/common/ViewToggle";
import { normalizeSkill } from "@/utils/skillNormalization";
import { matchesJobSearch } from "@/utils/searchUtils";

interface Job {
  id: string;
  title: string;
  company_id: string;
  company_name?: string;
  skills?: string[];
  description?: string;
}

interface JobSearchResultsProps {
  searchQuery: string;
  onResultClick: (id: string) => void;
  viewMode: ViewMode;
}

export function JobSearchResults({ searchQuery, onResultClick, viewMode }: JobSearchResultsProps) {
  const [jobs, setJobs] = useState<Job[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalJobs, setTotalJobs] = useState(0);
  const resultsPerPage = 5;

  useEffect(() => {
    // Reset to first page on new search
    setCurrentPage(1);
  }, [searchQuery]);

  useEffect(() => {
    const searchJobs = async () => {
      if (!searchQuery.trim()) return;

      setIsLoading(true);
      
      try {
        // Get all jobs for client-side universal search
        const { data: allJobs, error } = await supabase
          .from("jobs")
          .select("*, company:company_id(name)")
          .order("title", { ascending: true });
          
        if (error) throw error;
        
        // Filter using universal search
        const filteredJobs = (allJobs || []).filter(job => 
          matchesJobSearch({
            ...job,
            company: Array.isArray(job.company) ? job.company[0] : job.company,
            skills: job.skills || [],
            hardSkills: [],
            softSkills: [],
            employmentType: null,
            timeCommitment: null,
            seniorityLevel: null
          }, searchQuery)
        );
        
        setTotalJobs(filteredJobs.length);
        
        // Apply pagination to the filtered results
        const startIndex = (currentPage - 1) * resultsPerPage;
        const paginatedResults = filteredJobs.slice(startIndex, startIndex + resultsPerPage);
        
        // Format the results to include company name
        const formattedJobs = paginatedResults.map(job => ({
          ...job,
          company_name: job.company?.name
        }));
        
        setJobs(formattedJobs);
      } catch (error) {
        console.error("Error searching jobs:", error);
      } finally {
        setIsLoading(false);
      }
    };

    searchJobs();
  }, [searchQuery, currentPage]);

  const totalPages = Math.ceil(totalJobs / resultsPerPage);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    // Direct scroll without any transition
    const scrollableParent = document.querySelector('.overflow-y-auto') as HTMLElement;
    if (scrollableParent) {
      // Force immediate scroll without animation
      scrollableParent.style.scrollBehavior = 'auto';
      scrollableParent.scrollTop = 0;
      scrollableParent.style.scrollBehavior = '';
    }
  };

  if (isLoading) {
    return <div className="flex items-center justify-center h-40 text-gray-500">
      <div className="animate-spin mr-2 h-5 w-5 border-2 border-digger-blue border-t-transparent rounded-full"></div>
      Searching jobs...
    </div>;
  }

  if (jobs.length === 0) {
    return <div className="flex flex-col items-center justify-center h-40 text-gray-500">
      <Briefcase className="h-12 w-12 mb-2 text-gray-400" />
      <p>No jobs found matching "<span className="font-medium">{searchQuery}</span>"</p>
    </div>;
  }

  const renderPagination = () => {
    if (totalPages <= 1) return null;
    
    return (
      <div className="sticky bottom-0 z-10 bg-white dark:bg-gray-800">
        <Pagination>
          <PaginationContent>
            <PaginationItem>
              <PaginationPrevious 
                onClick={() => handlePageChange(Math.max(currentPage - 1, 1))}
                className={currentPage === 1 ? "pointer-events-none opacity-50" : "cursor-pointer"}
              />
            </PaginationItem>
            
            {[...Array(Math.min(3, totalPages))].map((_, i) => {
              const pageNumber = i + 1;
              return (
                <PaginationItem key={pageNumber}>
                  <PaginationLink
                    onClick={() => handlePageChange(pageNumber)}
                    isActive={currentPage === pageNumber}
                    className="cursor-pointer"
                  >
                    {pageNumber}
                  </PaginationLink>
                </PaginationItem>
              );
            })}
            
            {totalPages > 3 && (
              <PaginationItem>
                <PaginationLink
                  onClick={() => handlePageChange(totalPages)}
                  isActive={currentPage === totalPages}
                  className="cursor-pointer"
                >
                  {totalPages}
                </PaginationLink>
              </PaginationItem>
            )}
            
            <PaginationItem>
              <PaginationNext 
                onClick={() => handlePageChange(Math.min(currentPage + 1, totalPages))}
                className={currentPage === totalPages ? "pointer-events-none opacity-50" : "cursor-pointer"}
              />
            </PaginationItem>
          </PaginationContent>
        </Pagination>
      </div>
    );
  };

  return (
    <div className="flex flex-col h-full">
      {viewMode === "card" ? (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4">
            {jobs.map((job) => (
              <div
                key={job.id}
                className="p-4 border rounded-sm hover:bg-gray-50 cursor-pointer transition-colors shadow-sm hover:shadow"
                onClick={() => onResultClick(job.id)}
              >
                <div className="flex items-start mb-2">
                  <div className="h-10 w-10 bg-gray-100 rounded-sm flex items-center justify-center mr-3 text-digger-blue">
                    <Briefcase className="h-5 w-5" />
                  </div>
                  <div>
                    <h3 className="font-medium text-digger-blue">{job.title}</h3>
                    {job.company_name && (
                      <p className="text-gray-600 text-sm flex items-center mt-1">
                        <Building className="h-3.5 w-3.5 mr-1 inline-block" />
                        {job.company_name}
                      </p>
                    )}
                  </div>
                </div>
                
                {job.skills && job.skills.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-1">
                    {job.skills.slice(0, 5).map((skill) => (
                      <Badge key={skill} variant="secondary">{skill}</Badge>
                    ))}
                    {job.skills.length > 5 && (
                      <span className="text-xs text-gray-500 ml-1 self-center">
                        +{job.skills.length - 5} more
                      </span>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
          {renderPagination()}
        </>
      ) : (
        <>
          <div className="overflow-hidden">
            <div className="grid grid-cols-12 bg-muted px-4 py-2.5 text-sm font-medium sticky top-0">
              <div className="col-span-5">Title</div>
              <div className="col-span-3">Company</div>
              <div className="col-span-4">Skills</div>
            </div>
            
            <div>
              {jobs.map((job) => (
                <div 
                  key={job.id} 
                  className="grid grid-cols-12 px-4 py-3 border-b hover:bg-muted/50 cursor-pointer transition-colors"
                  onClick={() => onResultClick(job.id)}
                >
                  <div className="col-span-5 flex items-center gap-2">
                    <div className="h-8 w-8 bg-gray-100 rounded-sm flex items-center justify-center text-digger-blue">
                      <Briefcase className="h-4 w-4" />
                    </div>
                    <span className="font-medium">{job.title}</span>
                  </div>
                  <div className="col-span-3 flex items-center gap-2">
                    {job.company_name ? (
                      <>
                        <Building className="h-4 w-4 text-gray-400" />
                        {job.company_name}
                      </>
                    ) : (
                      "-"
                    )}
                  </div>
                  <div className="col-span-4 flex flex-wrap gap-1 items-center">
                    {job.skills && job.skills.slice(0, 3).map((skill) => (
                      <Badge key={skill} variant="secondary">{skill}</Badge>
                    ))}
                    {job.skills && job.skills.length > 3 && (
                      <span className="text-xs text-gray-500">
                        +{job.skills.length - 3} more
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
          {renderPagination()}
        </>
      )}
    </div>
  );
}
